import React from 'react';
import {
  Image,
  Text,
  View,
  Page,
  Document,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';
import {
  normalizeFinalEvaluationData,
  resolveImageSrc,
  formatDocDate,
  FALLBACK_EVAL_SIGNATURE,
} from './documentTemplateDefaults';

const hyphenationCallback = (word) => [word];

// Template Hasil Penilaian Akhir untuk formula v2 (mis. program BUMDes).
// Komponen nilainya berbeda dari v1: HANYA Klasifikasi Hardskill (KPI),
// Penilaian Antar Teman (PAT), dan Kehadiran (Absensi) -- tidak ada
// Softskill/BCF. Bobot mengikuti CLPRefreshEvaluationsFinalUseCase
// (_refreshFormulaV2): Absensi 35% + KPI 40% + PAT 25%.
const FormulaV2Template = ({ finalEvaluation: rawFinalEvaluation }) => {
  const finalEvaluation = normalizeFinalEvaluationData(rawFinalEvaluation);
  const documentTemplate = finalEvaluation?.documentTemplate || {};
  // Filter logo yang belum punya `url` (mis. baris "Tambah Logo" yang belum
  // diunggah gambarnya) -- kalau tetap dirender, <Image src={undefined}>
  // membuat react-pdf gagal decode & melempar error internal yang kriptik.
  // Logo dikelompokkan kiri/kanan berdasarkan `align` -- yang belum diisi
  // (data lama sebelum fitur ini ada) dianggap kiri, konsisten dgn posisi
  // aslinya sebelum dipisah eksplisit.
  const allLogos = documentTemplate.evalLogos?.filter((logo) => logo?.url) || [];
  const leftLogos = allLogos.filter((logo) => logo.align !== 'right');
  const rightLogos = allLogos.filter((logo) => logo.align === 'right');
  const dateText = formatDocDate(documentTemplate.docPlace, documentTemplate.evalDocDate, 'Jakarta');
  const signatureSrc = resolveImageSrc(documentTemplate.evalSignatureUrl, FALLBACK_EVAL_SIGNATURE);
  const signerName = documentTemplate.evalSignerName;
  const signerTitle = documentTemplate.evalSignerTitle || 'CEO Bakrie Center Foundation';

  Font.register({
    family: 'OpenSans',
    fonts: [
      { src: `/font/OpenSans-Regular.ttf` },
      { src: `/font/OpenSans-Bold.ttf`, fontWeight: 'bold' },
      { src: `/font/OpenSans-Italic.ttf`, fontWeight: 'normal', fontStyle: 'italic' },
      { src: `/font/OpenSans-BoldItalic.ttf`, fontWeight: 'bold', fontStyle: 'italic' },
    ],
  });

  Font.registerHyphenationCallback(hyphenationCallback);

  const styles = StyleSheet.create({
    page: {
      fontSize: 11,
      paddingBottom: 40,
      paddingTop: 40,
      paddingLeft: 40,
      paddingRight: 40,
      lineHeight: 1.5,
      flexDirection: 'column',
    },
    spaceBetween: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      color: '#3E3E3E',
    },
    spaceEvenly: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      color: '#3E3E3E',
    },
    titleContainer: { flexDirection: 'row', marginTop: 24 },
    logo: { width: 200 },
    table: {
      display: 'table',
      width: 'auto',
      borderRightWidth: 0,
      borderBottomWidth: 0,
      marginTop: 10,
    },
    tableRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    tableColHeader: {
      fontSize: 11,
      fontFamily: 'OpenSans',
      fontWeight: 'bold',
      height: 30,
      textAlign: 'center',
      justifyContent: 'center',
      backgroundColor: '#1366A8',
      color: '#FFF',
      borderColor: 'whitesmoke',
      borderRightWidth: 1,
      borderBottomWidth: 1,
    },
    tableColBody: {
      textAlign: 'center',
      height: '100%',
      justifyContent: 'center',
      padding: 5,
      fontSize: 11,
      borderColor: 'whitesmoke',
      borderRightWidth: 1,
      borderBottomWidth: 1,
    },
    tableFinal: {
      backgroundColor: '#1366A8',
      color: '#FFF',
      width: '100%',
      fontSize: 11,
      fontFamily: 'OpenSans',
      fontWeight: 'bold',
    },
    textContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
    },
  });

  const truncateNote = (note, limit = 20) => {
    if (!note) return '';
    return note.split(' ').slice(0, limit).join(' ')
      + (note.split(' ').length > limit ? '...' : '');
  };

  const formatScore = (value) => (value === null || value === undefined ? '-' : Number(value).toFixed(2));

  const HeaderLogos = () => (
    <View style={styles.titleContainer}>
      <View style={[styles.spaceBetween, { marginTop: -30, marginBottom: 10 }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          {leftLogos.map((logo, index) => (
            <Image
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={[styles.logo, logo.width ? { width: logo.width } : null]}
              src={resolveImageSrc(logo.url)}
            />
          ))}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          {rightLogos.map((logo, index) => (
            <Image
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={[styles.logo, logo.width ? { width: logo.width } : null]}
              src={resolveImageSrc(logo.url)}
            />
          ))}
        </View>
      </View>
    </View>
  );

  // Program: BUMDES (Bumdes/The New CLP) memakai judul "COLLABORATIVE
  // LEADERSHIP PROGRAM", sedang CLP (Campus Leaders Program) tetap memakai
  // judul lama -- lihat kode program di master_second_tier_program.
  const isBumdesProgram = finalEvaluation?.programCode === 'BUMDES';

  const Title = () => (
    <View>
      <Text style={{ fontSize: 14, textAlign: 'center', fontFamily: 'OpenSans', fontWeight: 'bold' }}>
        Penilaian Akhir
      </Text>
      <Text style={{ fontSize: 14, textAlign: 'center', fontFamily: 'OpenSans', fontWeight: 'bold' }}>
        {isBumdesProgram ? 'COLLABORATIVE LEADERSHIP PROGRAM' : 'Magang Campus Leaders Program'}
      </Text>
      <View style={styles.textContainer}>
        <Text style={{ fontSize: 12, fontFamily: 'OpenSans', fontWeight: 'bold', paddingTop: 10 }}>
          (Final Assessment)
        </Text>
      </View>
    </View>
  );

  const Detail = () => (
    <View style={{ flexDirection: 'row', marginTop: 10 }}>
      <View style={styles.spaceEvenly}>
        <View>
          <Text>Nama Lengkap </Text>
          <Text>Asal Universitas </Text>
          <Text>Jurusan </Text>
          <Text>Divisi </Text>
        </View>
        <View>
          <Text>: </Text>
          <Text>: </Text>
          <Text>: </Text>
          <Text>: </Text>
        </View>
        <View>
          <Text>
            {finalEvaluation?.mentessFirstName} {finalEvaluation?.mentessLastName}
          </Text>
          <Text>{finalEvaluation?.universities?.universitiesName}</Text>
          <Text>{finalEvaluation?.universities?.universitiesMajor}</Text>
          <Text>{finalEvaluation?.institutions?.divisionName}</Text>
        </View>
      </View>
    </View>
  );

  const TableHardskill = () => (
    <View style={styles.table}>
      <View style={styles.tableRow} wrap={false}>
        <View style={[{ width: '20%', borderLeftWidth: 1 }, styles.tableColHeader]}>
          <Text>NO</Text>
        </View>
        <View style={[{ width: '60%' }, styles.tableColHeader]}>
          <Text>Klasifikasi Hard Skill</Text>
        </View>
        <View style={[{ width: '40%' }, styles.tableColHeader]}>
          <Text>Nilai Akhir</Text>
        </View>
        <View style={[{ width: '100%' }, styles.tableColHeader]}>
          <Text>Catatan</Text>
        </View>
      </View>
      {finalEvaluation?.evaluationsHS?.map((item, key) => (
        <View style={styles.tableRow} key={item.id ?? key} wrap={false}>
          <View style={[{ width: '20%', borderLeftWidth: 1 }, styles.tableColBody]}>
            <Text>{key + 1}</Text>
          </View>
          <View style={[{ width: '60%' }, styles.tableColBody]}>
            <Text style={{ textAlign: 'justify' }}>
              KPI {key + 1}: {item.classification}
            </Text>
          </View>
          <View style={[{ width: '40%' }, styles.tableColBody]}>
            <Text>{item.evaluation ?? item.evaluationTotal}</Text>
          </View>
          <View style={[{ width: '100%' }, styles.tableColBody]}>
            <Text style={{ textAlign: 'justify' }}>{truncateNote(item.note)}</Text>
          </View>
        </View>
      ))}
    </View>
  );

  // Komponen di luar KPI: Penilaian Antar Teman (PAT) & Kehadiran (Absensi),
  // sesuai bobot formula v2 (CLPRefreshEvaluationsFinalUseCase._refreshFormulaV2).
  const TableOtherComponents = () => (
    <View style={styles.table}>
      <View style={styles.tableRow} wrap={false}>
        <View style={[{ width: '20%', borderLeftWidth: 1 }, styles.tableColHeader]}>
          <Text>NO</Text>
        </View>
        <View style={[{ width: '60%' }, styles.tableColHeader]}>
          <Text>Komponen Penilaian</Text>
        </View>
        <View style={[{ width: '40%' }, styles.tableColHeader]}>
          <Text>Nilai</Text>
        </View>
      </View>
      <View style={styles.tableRow} wrap={false}>
        <View style={[{ width: '20%', borderLeftWidth: 1 }, styles.tableColBody]}>
          <Text>1</Text>
        </View>
        <View style={[{ width: '60%' }, styles.tableColBody]}>
          <Text style={{ textAlign: 'justify' }}>Penilaian Antar Teman (PAT) - bobot 25%</Text>
        </View>
        <View style={[{ width: '40%' }, styles.tableColBody]}>
          <Text>{formatScore(finalEvaluation?.patScore)}</Text>
        </View>
      </View>
      <View style={styles.tableRow} wrap={false}>
        <View style={[{ width: '20%', borderLeftWidth: 1 }, styles.tableColBody]}>
          <Text>2</Text>
        </View>
        <View style={[{ width: '60%' }, styles.tableColBody]}>
          <Text style={{ textAlign: 'justify' }}>Kehadiran (Absensi) - bobot 35%</Text>
        </View>
        <View style={[{ width: '40%' }, styles.tableColBody]}>
          <Text>{formatScore(finalEvaluation?.attendanceScore)}</Text>
        </View>
      </View>
      <View style={styles.tableRow}>
        <View style={[styles.tableColBody, styles.tableFinal, { borderLeftWidth: 1, width: '80%' }]}>
          <Text>Nilai Akhir (Absensi 35% + KPI 40% + PAT 25%)</Text>
        </View>
        <View style={[styles.tableColBody, styles.tableFinal, { width: '40%' }]}>
          <Text>{formatScore(finalEvaluation?.finalEvaluationAverage)}</Text>
        </View>
      </View>
    </View>
  );

  const DateAndSignature = () => (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 40,
      }}
    >
      <View style={{ alignItems: 'center' }}>
        <Text style={{ fontSize: 12, fontFamily: 'OpenSans', marginBottom: 5 }}>{dateText}</Text>
        <Text style={{ fontSize: 12, fontFamily: 'OpenSans', fontWeight: 'bold', marginBottom: 5 }}>
          Mengetahui,
        </Text>
        <Image style={{ width: documentTemplate.evalSignatureWidth || 180 }} src={signatureSrc} />
        {signerName && (
          <Text style={{ fontSize: 11, fontFamily: 'OpenSans', fontWeight: 'bold', marginTop: 2 }}>
            {signerName}
          </Text>
        )}
        <Text style={{ fontSize: 11, fontFamily: 'OpenSans', marginTop: 2 }}>{signerTitle}</Text>
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <HeaderLogos />
        <Title />
        <Detail />
        <TableHardskill />
        <TableOtherComponents />
        <DateAndSignature />
      </Page>
    </Document>
  );
};

export default FormulaV2Template;
